import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useDispatch, useSelector } from 'react-redux';
import { flow } from 'lodash';

import PaymentOptionsTable from './PaymentOptionsTable.component';

import tablePayment from '../store/tablePayment';
import fees from '../store/fees';
import clients from '../store/clients';
import transactions from '../store/transactions';

const enumTipoTransacao = {
  client: 3,
  ec: 2,
};

const formatToCurrency = (number) =>
  Number(number).toLocaleString('pt-br', {
    style: 'currency',
    currency: 'BRL',
  });

const TablePaymentComponent = ({ onlyLink = false }) => {
  const dispatch = useDispatch();

  const dispatchRedux = {
    resetState: flow(tablePayment.action.resetState, dispatch),
    updateInstallmentSelected: flow(
      tablePayment.action.updateInstallmentSelected,
      dispatch
    ),
    feeServiceGetLink: flow(fees.action.serviceGetLink, dispatch),
  };

  const selectorRedux = {
    dataClients: useSelector(clients.selector.selectPayload),
    amount: useSelector(tablePayment.selector.selectAmount),
    installmentSelected: useSelector(
      tablePayment.selector.selectInstallmentSelected
    ),
    typeTax: useSelector(tablePayment.selector.selectTypeTax),
    return: useSelector(tablePayment.selector.selectReturn),
    feesData: useSelector(fees.selector.selectData),
    feesDataLink: useSelector(fees.selector.selectDataLink),
    feesLoading: useSelector(fees.selector.selectLoading),
    transactionsLoading: useSelector(transactions.selector.selectLoading),
  };

  React.useEffect(() => {
    return () => {
      dispatchRedux.resetState();
    };
  }, []);

  React.useEffect(() => {
    if (onlyLink) dispatchRedux.feeServiceGetLink();
  }, [onlyLink]);

  const { retornoGiro } = selectorRedux.dataClients || {};

  const idTipoTransacao = enumTipoTransacao[selectorRedux.typeTax];

  const installments = onlyLink
    ? selectorRedux.feesDataLink?.filter(
        (f) => f.cdTipoTransacao === idTipoTransacao
      )
    : selectorRedux.feesData?.filter(
        (f) => f.cdTipoTransacao === idTipoTransacao
      );
  const rows = installments
    ?.filter((i) => i.cdTipoTaxa != 39)
    ?.map((r, idx) => {
      let installment_amount,
        amount,
        returnAmount = 0;

      const isDebit = r.cdTipoTaxa === 1;
      const isPix = r.cdTipoTaxa === 3;

      if (selectorRedux.return > 0) {
        const returnPlusGiro = selectorRedux.return >= 8 ? 0.5 : 0;
        const returnEc = Number(selectorRedux.return);
        const returnGiro = retornoGiro ? returnEc * 0.25 + returnPlusGiro : 0;

        returnAmount =
          isDebit || isPix
            ? 0
            : selectorRedux.amount *
              ((selectorRedux.return + returnGiro) / 100);
      }

      if (selectorRedux.typeTax === 'ec') {
        installment_amount = selectorRedux.amount / r.nuParcelas;
        amount = r.vlFator * selectorRedux.amount * r.nuParcelas;
        if (isPix) {
          const limitPix = selectorRedux?.feesData.find(
            (i) => i.cdTipoTaxa === 39
          );
          if (limitPix) {
            amount =
              amount < selectorRedux.amount - limitPix.vlFator
                ? amount
                : selectorRedux.amount - limitPix.vlFator;
            amount =
              selectorRedux.amount - amount > limitPix.txCET
                ? selectorRedux.amount - limitPix.txCET
                : amount;
            installment_amount = selectorRedux.amount;
          }
        }
      }

      if (selectorRedux.typeTax === 'client') {
        installment_amount = r.vlFator * (selectorRedux.amount + returnAmount);
        amount =
          r.vlFator * (selectorRedux.amount + returnAmount) * r.nuParcelas;
        if (isPix) {
          const limitPix = selectorRedux?.feesData.find(
            (i) => i.cdTipoTaxa === 39
          );
          if (limitPix) {
            amount =
              amount < selectorRedux.amount + limitPix.vlFator
                ? selectorRedux.amount + limitPix.vlFator
                : amount;
            amount =
              amount - selectorRedux.amount > limitPix.txCET
                ? selectorRedux.amount + limitPix.txCET
                : amount;
            installment_amount = amount;
          }
        }
      }

      const installment = isDebit || isPix ? 'Única' : r.nuParcelas;

      return {
        key: `installment_${idx}`,
        type: isPix ? 'PIX' : isDebit ? 'Débito' : 'Crédito',
        installment,
        installment_amount: formatToCurrency(installment_amount),
        amount: formatToCurrency(amount),
        amount_number: amount,
        installment_amount_number: installment_amount,
      };
    });

  const hasPix = rows?.some((r) => r.type === 'PIX');
  const hasDebit = rows?.some((r) => r.type === 'Débito');

  return (
    <Box position="relative" overflow="hidden">
      <Box
        display="flex"
        flexDirection="column"
        gap={0.5}
        pr={2}
        position="sticky"
        top={0}
      >
        <PaymentOptionsTable
          rows={rows}
          maxInstallments={
            installments?.length - (hasPix ? 2 : hasDebit ? 1 : 0)
          }
          totalLabel={
            selectorRedux.typeTax === 'client'
              ? 'Valor total'
              : 'Valor liberado'
          }
          selectedKey={selectorRedux.installmentSelected?.key}
          onSelect={dispatchRedux.updateInstallmentSelected}
        />

        {selectorRedux.amount === 0 && (
          <Box
            position="absolute"
            top={0}
            left={0}
            height="100%"
            minHeight="500px"
            bgcolor="#ffffffe0"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography color="#808080">Informe um valor</Typography>
          </Box>
        )}

        {selectorRedux.transactionsLoading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            height="100%"
            minHeight="500px"
            bgcolor="#ffffffe0"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          />
        )}

        {selectorRedux.feesLoading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            height="100%"
            minHeight="500px"
            bgcolor="white"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TablePaymentComponent;
